import type { FieldMetadata, FieldValue } from '../../data/client';
import { isSelectableField } from '../../fields/fieldTypeMeta';

/**
 * FIELD GROUPS ON THE RECORD PAGE (D8, D9), as a pure projection of `position` and
 * `group_label`.
 *
 * The problem this solves is the first one section 0 names: a twenty-seven field
 * agency renders as twenty-seven identical rows, and nothing on the page says which of
 * them belong together. Grouping alone turns that into six scannable blocks.
 *
 * GROUP ORDER IS THE POSITION OF THE GROUP'S FIRST FIELD (D9), not alphabetical and not
 * a second stored number. That means moving a group is moving its first field, which
 * the admin module's move buttons already do - no new control, no second ordering to
 * keep in step with `position`.
 */

export interface FieldGroup {
  /** The group label, or `null` for the trailing ungrouped section. */
  readonly label: string | null;
  readonly fields: readonly FieldMetadata[];
}

/**
 * Fields in `position` order, partitioned into groups in first-member order, with the
 * ungrouped ones LAST in a section that has no heading.
 *
 * Ungrouped goes last rather than first for a reason that only shows on a half-grouped
 * object: while an admin is still assigning labels, the fields they have not reached
 * yet must not push the ones they have already organised down the page.
 *
 * An object where NOTHING is grouped therefore yields exactly one unlabelled group,
 * which the renderer draws as the flat list it was before - so grouping is invisible
 * until somebody uses it.
 */
export function fieldGroups(fields: readonly FieldMetadata[]): readonly FieldGroup[] {
  const ordered = [...fields]
    // A MANY_TO_MANY is a SECTION, not a row (D3, and D9's own note beside `csm`): the
    // server returns null for it at HTTP 200, so the row would show an em dash no matter
    // how many people the agency has. `isSelectableField` states the whole reason.
    .filter(isSelectableField)
    .toSorted((a, b) => a.position - b.position);
  const grouped = new Map<string, FieldMetadata[]>();
  const ungrouped: FieldMetadata[] = [];

  for (const field of ordered) {
    const label = normaliseLabel(field.groupLabel);
    if (label === null) {
      ungrouped.push(field);
      continue;
    }
    const bucket = grouped.get(label);
    if (bucket === undefined) {
      // Insertion order IS first-member order, because `ordered` is sorted by position.
      grouped.set(label, [field]);
      continue;
    }
    bucket.push(field);
  }

  const groups: FieldGroup[] = [...grouped].map(([label, members]) => ({ label, fields: members }));
  if (ungrouped.length > 0) groups.push({ label: null, fields: ungrouped });
  return groups;
}

/**
 * `group_label` is FREE TEXT on the server and will be typo'd (open risk 5). Trimming
 * here means "Identity" and "Identity " are one section rather than two identical
 * headings; an empty string is the same statement as null and is treated as one.
 */
function normaliseLabel(label: string | undefined): string | null {
  if (label === undefined) return null;
  const trimmed = label.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Is this field EMPTY on this record - the question the empty-field fold asks (handbook
 * DESIGN.md H4).
 *
 * An empty array counts as empty (a MULTI_SELECT with nothing chosen), and so does an
 * empty string. `false` and `0` do NOT: a BOOLEAN that is off and a number that is
 * zero are answers, and folding them away would hide information rather than tidy it.
 */
export function isEmptyValue(value: FieldValue | undefined): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Where a field stands on the record: in its group, or in the empty-field fold. */
export type FieldPlacement = 'open' | 'folded';

/** The rows under the band, split into what stands open and what the fold holds. */
export interface FoldedGroups {
  /** Every group with something to show, its folded fields taken out. */
  readonly shown: readonly FieldGroup[];
  /** The folded fields, still under their own group's name, in the groups' order. */
  readonly folded: readonly FieldGroup[];
  /** How many folded fields are empty: the number in "12 empty fields". */
  readonly emptyCount: number;
}

/**
 * EMPTY FIELDS FOLD INTO ONE LINE (BR-RECORD; handbook DESIGN.md H4: "fold empty record fields
 * into one line ("12 empty fields") that opens them", N21; register A6-13: "empty fields fold").
 *
 * It replaces the "Hide empty fields" checkbox, which was OFF by default, so a freshly imported
 * agency opened as twenty rows of dashes. Now an empty field never stands open: it waits in one
 * line at the end, still under its own group's name, and is one click from being filled.
 *
 * `keepOpen` names the fields whose EMPTINESS IS THE MESSAGE and which therefore never fold: a
 * checkpoint with no date is a step not done (and maybe late), and the health block's fields ask
 * for the judgement right under it. The caller decides which those are, because both are decided
 * by configuration the caller reads (D3, D17), not by this projection.
 *
 * `pinned` names the fields a person is working on, with the place they stood when the work
 * began: a field never moves between the fold and its group in the middle of an edit or an
 * upload, because moving it remounts its control and loses what was half done (an upload not
 * yet confirmed, a value not yet saved). The split catches up on the next visit.
 *
 * A group that keeps nothing open is DROPPED from `shown` rather than drawn as a heading over
 * nothing.
 */
export function foldEmptyFields(
  groups: readonly FieldGroup[],
  values: Readonly<Record<string, FieldValue>>,
  keepOpen: (field: FieldMetadata) => boolean = () => false,
  pinned: ReadonlyMap<string, FieldPlacement> = new Map(),
): FoldedGroups {
  const shown: FieldGroup[] = [];
  const folded: FieldGroup[] = [];
  let emptyCount = 0;
  const placementOf = (field: FieldMetadata): FieldPlacement =>
    pinned.get(field.id) ??
    (keepOpen(field) || !isEmptyValue(values[field.id]) ? 'open' : 'folded');
  for (const group of groups) {
    const open = group.fields.filter((field) => placementOf(field) === 'open');
    const fold = group.fields.filter((field) => placementOf(field) === 'folded');
    if (open.length > 0) shown.push({ label: group.label, fields: open });
    if (fold.length > 0) folded.push({ label: group.label, fields: fold });
    emptyCount += fold.filter((field) => isEmptyValue(values[field.id])).length;
  }
  return { shown, folded, emptyCount };
}

/**
 * The fold's line, in the handbook's own words ("12 empty fields", DESIGN.md H4) with a real
 * singular (D14: "never "(s)"").
 */
export function emptyFieldsLabel(count: number): string {
  return count === 1 ? '1 empty field' : `${count} empty fields`;
}

/**
 * THE ROWS THE IDENTITY BAND ALREADY SHOWS, TAKEN OUT OF THE GROUPS UNDER IT.
 *
 * DECISION P5-W3-1 (wave 3, PH-3) made this for the phone's Overview; BR-RECORD makes it the
 * rule wherever a band stands above the rows (register A6-13: header facts "never repeat
 * below"). A group whose every field is in the band disappears rather than standing as a
 * heading over nothing.
 *
 * ONLY A CALLER THAT DRAWS THE BAND PASSES IT, because a value taken out of the rows has to be
 * reachable somewhere: on the desktop page and in the list's pane the band's values are
 * controls (`KeyFactBand`), and the phone's Details segment, which has no band, keeps every
 * row.
 */
export function withoutFields(
  groups: readonly FieldGroup[],
  hidden: readonly string[],
): readonly FieldGroup[] {
  if (hidden.length === 0) return groups;
  const drop = new Set(hidden);
  return groups
    .map((group) => ({
      label: group.label,
      fields: group.fields.filter((field) => !drop.has(field.id)),
    }))
    .filter((group) => group.fields.length > 0);
}
